/**
 * Form column catalogue — turns a parsed FormDefinition into the metadata the
 * explorer needs to render the column-picker tree, type-aware cells, and
 * client-side calculated fields.
 *
 * Pure (no React / Supabase) → unit-testable.
 */
import type {
  FormDefinition,
  FieldDefinition,
  FieldType,
  SelectOption,
  CalculatedField,
  SubFieldDefinition,
} from '@/features/form-builder/api/form-builder-types'

export interface CatalogField {
  fieldKey: string
  fieldType: FieldType
  label: string
  /** the raw definition — passed to form-engine's formatValueForDisplay. */
  definition: FieldDefinition
  /** option_key → fa label, for select cells. */
  options?: Record<string, string>
  isRepeatableArray: boolean
  subFields?: CatalogField[]
  /** file/image cannot be tabularised server-side; flagged for the UI. */
  isFileLike: boolean
}

export interface FormCatalog {
  formId: string
  /** ordered, top-level fields (calc fields excluded — see calculated). */
  fields: CatalogField[]
  byKey: Record<string, CatalogField>
  /** calculated fields, computed client-side. */
  calculated: CalculatedField[]
  calculatedByKey: Record<string, CalculatedField>
  /** all FieldDefinitions (incl. calc as synthetic) for form-engine compute. */
  allFieldDefs: FieldDefinition[]
}

function optionsMap(field: FieldDefinition): Record<string, string> | undefined {
  const opts = field.type_config?.options as SelectOption[] | undefined
  if (!opts || opts.length === 0) return undefined
  const map: Record<string, string> = {}
  for (const o of opts) map[o.option_key] = o.label?.fa ?? o.option_key
  return map
}

function subFieldToCatalog(sf: SubFieldDefinition): CatalogField {
  const def: FieldDefinition = {
    field_id: sf.field_id,
    field_key: sf.field_key,
    field_type: sf.field_type,
    label: sf.label,
    is_required: sf.is_required,
    validations: [],
    type_config: sf.type_config ?? {},
  }
  return {
    fieldKey: sf.field_key,
    fieldType: sf.field_type,
    label: sf.label?.fa || sf.field_key,
    definition: def,
    options: optionsMap(def),
    isRepeatableArray: false,
    isFileLike: sf.field_type === 'file' || sf.field_type === 'image',
  }
}

function fieldToCatalog(field: FieldDefinition): CatalogField {
  const isArray = field.field_type === 'repeatable_array'
  const subDefs = (field.type_config?.sub_fields as SubFieldDefinition[] | undefined) ?? []
  return {
    fieldKey: field.field_key,
    fieldType: field.field_type,
    label: field.label?.fa || field.field_key,
    definition: field,
    options: optionsMap(field),
    isRepeatableArray: isArray,
    subFields: isArray ? subDefs.map(subFieldToCatalog) : undefined,
    isFileLike: field.field_type === 'file' || field.field_type === 'image',
  }
}

/** Synthesise a FieldDefinition for a calculated field so form-engine can compute it. */
function calcToFieldDef(c: CalculatedField): FieldDefinition {
  return {
    field_id: c.field_key,
    field_key: c.field_key,
    field_type: 'calculated',
    label: c.label,
    is_required: false,
    validations: [],
    type_config: {
      formula: c.formula,
      result_type: c.result_type,
      decimal_places: c.decimal_places,
    },
  }
}

export function buildFormCatalog(formId: string, def: FormDefinition): FormCatalog {
  const fields = def.fields.map(fieldToCatalog)
  const byKey: Record<string, CatalogField> = {}
  for (const f of fields) byKey[f.fieldKey] = f

  const calculated = def.calculated_fields ?? []
  const calculatedByKey: Record<string, CalculatedField> = {}
  for (const c of calculated) calculatedByKey[c.field_key] = c

  const allFieldDefs: FieldDefinition[] = [
    ...def.fields,
    ...calculated.map(calcToFieldDef),
  ]

  return { formId, fields, byKey, calculated, calculatedByKey, allFieldDefs }
}

/** Field types that can be filtered/sorted server-side (have a scalar JSONB path). */
export const SERVER_QUERYABLE_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  'text', 'textarea', 'number', 'date', 'time', 'datetime',
  'single_select', 'multi_select', 'boolean',
])

export function isServerQueryable(field: CatalogField): boolean {
  return SERVER_QUERYABLE_TYPES.has(field.fieldType)
}
